/**
 * Autonomy (ROADMAP Phase 12 groundwork, migration 0062): the autonomy audit report and the
 * owner's grants. One row per agent x tool pair that MAY run without a person, and beside it
 * every prerequisite as the view reckons it right now — the agent's level, the evaluation
 * history, today's count against the cap, the error rate against its ceiling, a demotion.
 * Nothing on this page promotes an agent to level 3 (a migration), turns the platform flag on
 * or releases the kill switch's autonomous-actions bit (both in Settings). It shows what
 * would have to be true, and lets the owner enable a grant, which is one of the four.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type AutonomyGrantJson, type AutonomyReportJson } from '../api.js';

function Check({ ok, label }: { ok: boolean; label: string }): ReactNode {
  return (
    <span className={`chip ${ok ? 'chip-ok' : 'chip-attention'}`} title={label}>
      {ok ? '✓' : '✗'} {label}
    </span>
  );
}

export function AutonomyPage(): ReactNode {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const report = useQuery({
    queryKey: ['autonomy'],
    queryFn: () => api.get<AutonomyReportJson>('/api/autonomy'),
  });
  const patch = useMutation({
    mutationFn: (args: { id: string; body: Record<string, unknown> }) =>
      api.patch<{ grant: AutonomyGrantJson }>(`/api/autonomy/${args.id}`, args.body),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['autonomy'] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
  });

  const platform = report.data?.platform;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Autonomy</h1>
          <p className="subtitle">
            Which agent may run which LOW-risk action without a person, and whether every
            prerequisite holds right now. Nothing runs on its own until the platform flag is on, the
            kill switch allows it, the agent is at level 3, and its grant is enabled with the
            evaluation history, cap and error rate in order. HIGH-risk actions are never eligible.
          </p>
        </div>
      </header>

      {platform && (
        <section className="card">
          <h2>Platform</h2>
          <p>
            <Check ok={platform.flagEnabled} label={`${platform.flag} flag on`} />{' '}
            <Check
              ok={!platform.autonomousActionsDisabled}
              label="kill switch allows autonomous actions"
            />{' '}
            <span className="small muted">
              Both are changed in <Link to="/settings">Settings</Link>, audited.
            </span>
          </p>
        </section>
      )}

      <section className="card">
        <h2>Grants</h2>
        {error && <p className="error">{error}</p>}
        {report.data && report.data.report.length === 0 && (
          <p className="muted">
            No grants. A grant is created for one agent and one LOW-risk tool, disabled, through the
            API; every agent stays at its current level until a migration says otherwise.
          </p>
        )}
        {report.data && report.data.report.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Agent · tool</th>
                  <th>Prerequisites</th>
                  <th className="num">Today</th>
                  <th className="num">Window</th>
                  <th>Evals</th>
                  <th>Ready</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {report.data.report.map((r) => (
                  <tr key={r.grant_id}>
                    <td>
                      <strong>{r.agent_key}</strong> · <code>{r.tool_key}</code>
                      <div className="small muted">
                        level {r.autonomy_level} · confidence ≥ {r.min_confidence} · cap{' '}
                        {r.daily_cap}/day · errors ≤ {r.max_error_rate} over {r.error_window_days}d
                      </div>
                      {r.demoted_at && (
                        <div className="small error">
                          Demoted {r.demoted_at.slice(0, 16)}: {r.demoted_reason}
                        </div>
                      )}
                    </td>
                    <td>
                      <Check ok={r.enabled} label="enabled" />{' '}
                      <Check ok={r.autonomy_level === 3} label="level 3" />{' '}
                      <Check ok={r.demoted_at === null} label="not demoted" />
                    </td>
                    <td className="num">
                      {r.executions_today}/{r.daily_cap}
                    </td>
                    <td className="num" title="failed / autonomous executions in the window">
                      {r.errors_window}/{r.executions_window} ({r.error_rate_window})
                    </td>
                    <td className="small">
                      <Check
                        ok={r.eval_prerequisite_met}
                        label={`last ${r.required_consecutive_runs} of ${r.required_suite}`}
                      />
                      <div className="muted">{r.eval_runs_considered} considered</div>
                    </td>
                    <td>
                      <span
                        className={`chip ${r.prerequisites_met ? 'chip-ok' : 'chip-attention'}`}
                      >
                        {r.prerequisites_met ? 'grant ready' : 'not ready'}
                      </span>
                    </td>
                    <td className="small">
                      <button
                        type="button"
                        className="link"
                        disabled={patch.isPending}
                        onClick={() =>
                          patch.mutate({ id: r.grant_id, body: { enabled: !r.enabled } })
                        }
                      >
                        {r.enabled ? 'Disable' : 'Enable'}
                      </button>
                      {r.demoted_at && (
                        <>
                          {' · '}
                          <button
                            type="button"
                            className="link"
                            disabled={patch.isPending}
                            onClick={() =>
                              patch.mutate({ id: r.grant_id, body: { clearDemotion: true } })
                            }
                          >
                            Clear demotion
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {report.data?.calc_method && (
          <p className="small muted" style={{ marginTop: 12 }}>
            {report.data.calc_method} · as of {report.data.as_of.slice(0, 19)}
          </p>
        )}
      </section>
    </div>
  );
}
